import { colors } from '@/theme';

/**
 * Roles carry no color in the domain — the UI derives a stable one from the
 * roleRef tlpId so avatars and dots agree everywhere, across sessions.
 *
 * IDENTITY HUES ONLY. This palette used to reach for `success[80]`,
 * `warning[80]` and `coral[80]`, which are the design system's SEMANTIC
 * colours: a role came out green, amber or red and read as "fine", "warning"
 * or "error" when it meant nothing of the kind. `coral[80]` is the worse case
 * — `#ff5a5f` is the same value as `error[80]`, so an error colour was
 * standing in for a person's job title. Semantic tokens state status; identity
 * is not a status.
 *
 * `fog[80]` is gone for a related reason: `AllocationEditor` uses `fog-60` two
 * lines from a role dot to mean "no role picked yet", and at that lightness a
 * real role wore the empty state's colour. `fog[100]` stays — the darkest
 * neutral reads as a deliberate choice beside the light grey of "unset", and
 * it is the one clearly distinguishable slot among otherwise similar indigos.
 *
 * The rest are the brand hue families (pink, purple, blue, maroon, navy) at
 * steps dark enough to carry white text, since these also fill avatars.
 */
const PALETTE: readonly string[] = [
  colors.pink[80],
  colors.purple[80],
  colors.blue[80],
  colors.maroon[80],
  colors.navy[100],
  colors.pink[100],
  colors.fog[100],
  colors.maroon[100],
];

/** FNV-1a — tiny, deterministic, good spread on short ids. */
const fnv1a = (text: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

/** Stable colour for ANY id-keyed entity (roles, projects, resources). */
export const stableColor = (id: string): string => PALETTE[fnv1a(id) % PALETTE.length]!;

export const roleColor = (roleTlpId: string): string => stableColor(roleTlpId);
